// Library Methods
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  TouchSensor,
  useSensor,
  useSensors,
  rectIntersection,
  DragEndEvent,
  DragStartEvent,
  UniqueIdentifier,
  CollisionDetection,
  PointerSensor,
} from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import ComparisonCard from "./ComparisonCard/ComparisonCard";
import SelectExamsFooter, {
  SelectExamsFooterStateType,
} from "./SelectExamsFooter/SelectExamsFooter";
import GridLayoutPopover, {
  GridLayoutValue,
  LARGE_SPANNING_LAYOUT_IDS,
  SMALL_SCREEN_SPANNING_FALLBACK,
} from "./GridLayoutPopover/GridLayoutPopover";
import { ChevronLeftIcon } from "src/assets/icons";

// Types
import { ExternalExamType } from "src/services/exams/types";

// Styles
import {
  ModalCustomCancel,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";
import { useTheme } from "@mui/material/styles";

// Contexts
import { useExternalExamsContext } from "src/contexts/exams/ExternalExamsContext";

type ComparisonScreenType = {
  selectedExamIds: Set<string>;
  orderedExamIds: Array<string>;
  onReorderExamIds: (activeId: string, overId: string) => void;
  onToggleExamSelection: (examId: string) => void;
  onViewExamDetails: (examId: string) => void;
  gridLayout: GridLayoutValue;
  onGridLayoutChange: (value: GridLayoutValue) => void;
  onBack: () => void;
};

type CardPlacement = {
  imageHeight?: string;
  gridColumn?: string;
  gridRow?: string;
};

const ComparisonScreen = ({
  selectedExamIds,
  orderedExamIds,
  onReorderExamIds,
  onToggleExamSelection,
  onViewExamDetails,
  gridLayout,
  onGridLayoutChange,
  onBack,
}: ComparisonScreenType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { surface, dividerBorder } = getSurfaceTokens(theme);
  const { sortableCards } = useExternalExamsContext();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Hide the largest grid layouts on smaller screen sizes
  useEffect(() => {
    if (!isSmallScreen) return;

    if (
      gridLayout.mode === "spanning" &&
      LARGE_SPANNING_LAYOUT_IDS.has(gridLayout.id)
    ) {
      onGridLayoutChange(SMALL_SCREEN_SPANNING_FALLBACK);
      return;
    }

    // Same row count, one fewer column -- the 3-column version of the same
    // arrangement rather than an unrelated layout.
    if (gridLayout.mode === "columns" && gridLayout.columns === 4) {
      onGridLayoutChange({ mode: "columns", columns: 3 });
      return;
    }

    if (gridLayout.mode === "grouped" && gridLayout.columns === 4) {
      onGridLayoutChange({
        mode: "grouped",
        rows: gridLayout.rows,
        columns: 3,
      });
    }
  }, [isSmallScreen, gridLayout, onGridLayoutChange]);

  // Sourced from the full (unfiltered) card list -- sidebar filters on the
  // Selection screen shouldn't hide an exam that's already been selected.
  const selectedExams = useMemo(
    () =>
      orderedExamIds
        .map((examId) => sortableCards.find((exam) => exam.id === examId))
        .filter((exam): exam is ExternalExamType => Boolean(exam)),
    [orderedExamIds, sortableCards]
  );

  // Comparison mode has no purpose with nothing selected -- send the user
  // back rather than leave them stranded on an empty screen.
  useEffect(() => {
    if (selectedExamIds.size === 0) {
      onBack();
    }
  }, [selectedExamIds, onBack]);

  // Double-clicking a SelectionCard to get here can easily land a stray
  // click/double-click on whatever ends up under the cursor once this
  // screen renders -- briefly ignore card clicks so that doesn't
  // accidentally fall straight through into a details screen.
  const [isClickCooldownActive, setIsClickCooldownActive] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => setIsClickCooldownActive(false), 1000);
    return () => clearTimeout(timeout);
  }, []);

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor)
  );

  // The "Select Exams" footer can take over the whole screen -- the
  // comparison grid hides while it's fullscreen.
  const [footerState, setFooterState] =
    useState<SelectExamsFooterStateType>("preview");
  const isFooterExpanded = footerState === "fullscreen";

  // Nothing left to add once every exam is already in the comparison --
  // hide the footer entirely rather than show an empty "Select Exams" bar.
  const hasExamsAvailableToSelect = useMemo(
    () => sortableCards.some((exam) => !selectedExamIds.has(exam.id)),
    [sortableCards, selectedExamIds]
  );

  useEffect(() => {
    if (!hasExamsAvailableToSelect) {
      setFooterState("preview");
    }
  }, [hasExamsAvailableToSelect]);

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    onReorderExamIds(String(active.id), String(over.id));

    setActiveId(null);
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  // Logic for "Vert" layouts
  const VERT_LAYOUT_TOTAL_HEIGHT_PX = 600;
  // "1 x Y" ("columns" mode) layouts -- a single row of Y columns, so it
  // gets a taller card than the multi-row "grouped" layouts.
  const COLUMNS_LAYOUT_IMAGE_HEIGHT_PX = 600;
  const CARD_CHROME_PX = 40;
  const GRID_GAP_PX = 8;
  const BLOCK_GAP_PX = 100;

  // Get spacing for the layout
  const blockLayoutSizing = useMemo(() => {
    const cellsPerBlock =
      gridLayout.mode === "columns"
        ? gridLayout.columns
        : gridLayout.mode === "grouped"
          ? gridLayout.columns * gridLayout.rows
          : gridLayout.mode === "asymmetric"
            ? gridLayout.columns * gridLayout.rowImageHeights.length
            : gridLayout.cells.length;

    const rowsPerBlock =
      gridLayout.mode === "columns"
        ? 1
        : gridLayout.mode === "grouped"
          ? gridLayout.rows
          : gridLayout.mode === "asymmetric"
            ? gridLayout.rowImageHeights.length
            : gridLayout.rowsPerBlock;

    const totalBlocks = Math.max(
      1,
      Math.ceil(selectedExams.length / cellsPerBlock)
    );

    // Only "spanning" layouts can have a cell that spans the whole block
    // (the "large image" in a "Vert" layout) -- its rows share a fixed
    // total height instead of each having their own explicit height.
    const isVertStyle =
      gridLayout.mode === "spanning" &&
      gridLayout.cells.some((cell) => cell.rowSpan === rowsPerBlock);

    const vertRowHeightPx = isVertStyle
      ? (VERT_LAYOUT_TOTAL_HEIGHT_PX - (rowsPerBlock - 1) * GRID_GAP_PX) /
        rowsPerBlock
      : null;

    const realRowHeights = Array.from({ length: rowsPerBlock }, (_, i) => {
      if (vertRowHeightPx !== null) {
        return `${vertRowHeightPx}px`;
      }

      const localRow = i + 1;
      const imageHeight =
        // "1 x Y" layouts (a single row of Y columns) get a taller card
        // than the multi-row "grouped" layouts, since there's only ever
        // one row competing for vertical space.
        gridLayout.mode === "columns"
          ? `${COLUMNS_LAYOUT_IMAGE_HEIGHT_PX}px`
          : gridLayout.mode === "grouped"
            ? "300px"
            : gridLayout.mode === "asymmetric"
              ? gridLayout.rowImageHeights[i]
              : gridLayout.cells.find(
                  (cell) => cell.rowStart === localRow && cell.rowSpan === 1
                )?.imageHeight ?? "300px";

      return `calc(${imageHeight} + ${CARD_CHROME_PX}px)`;
    });

    const blockWithGapTemplate = [...realRowHeights, `${BLOCK_GAP_PX}px`].join(
      " "
    );
    const lastBlockTemplate = realRowHeights.join(" ");

    const gridTemplateRows =
      totalBlocks > 1
        ? `${Array(totalBlocks - 1)
            .fill(blockWithGapTemplate)
            .join(" ")} ${lastBlockTemplate}`
        : lastBlockTemplate;

    // Every block after the first also needs to skip over the spacer row
    // before it when converting a local row number into this card's
    // actual line number in the overall grid.
    const rowStride = rowsPerBlock + 1;

    const dividerRows = Array.from({ length: totalBlocks - 1 }, (_, i) => {
      return i * rowStride + rowsPerBlock + 1;
    });

    return {
      gridTemplateRows,
      vertRowImageHeight:
        vertRowHeightPx !== null
          ? `${vertRowHeightPx - CARD_CHROME_PX}px`
          : undefined,
      rowStride,
      dividerRows,
    };
  }, [gridLayout, selectedExams.length]);

  // One placement per card
  const cardPlacements = useMemo<CardPlacement[]>(
    () =>
      Array.from({ length: selectedExams.length }, (_, index) => {
        switch (gridLayout.mode) {
          case "columns": {
            const blockIndex = Math.floor(index / gridLayout.columns);
            const rowStart = blockIndex * blockLayoutSizing.rowStride + 1;

            return {
              imageHeight: `${COLUMNS_LAYOUT_IMAGE_HEIGHT_PX}px`,
              gridRow: `${rowStart} / ${rowStart + 1}`,
            };
          }

          case "grouped": {
            const cellsPerBlock = gridLayout.columns * gridLayout.rows;
            const localRow =
              Math.floor((index % cellsPerBlock) / gridLayout.columns) + 1;
            const blockIndex = Math.floor(index / cellsPerBlock);
            const rowStart =
              blockIndex * blockLayoutSizing.rowStride + localRow;

            return { gridRow: `${rowStart} / ${rowStart + 1}` };
          }

          case "asymmetric": {
            const rowsPerBlock = gridLayout.rowImageHeights.length;
            const cellsPerBlock = gridLayout.columns * rowsPerBlock;
            const rowIndex =
              Math.floor((index % cellsPerBlock) / gridLayout.columns) %
              rowsPerBlock;
            const blockIndex = Math.floor(index / cellsPerBlock);
            const rowStart =
              blockIndex * blockLayoutSizing.rowStride + rowIndex + 1;

            return {
              imageHeight: gridLayout.rowImageHeights[rowIndex],
              gridRow: `${rowStart} / ${rowStart + 1}`,
            };
          }

          case "spanning": {
            const cellsPerBlock = gridLayout.cells.length;
            const cell = gridLayout.cells[index % cellsPerBlock];
            const blockIndex = Math.floor(index / cellsPerBlock);
            const rowStart =
              blockIndex * blockLayoutSizing.rowStride + cell.rowStart;

            return {
              imageHeight:
                cell.imageHeight ??
                (cell.rowSpan > 1
                  ? "100%"
                  : blockLayoutSizing.vertRowImageHeight),
              gridColumn: cell.gridColumn,
              gridRow: `${rowStart} / ${rowStart + cell.rowSpan}`,
            };
          }

          default:
            return {};
        }
      }),
    [gridLayout, selectedExams.length, blockLayoutSizing]
  );

  const fixCursorSnapOffset: CollisionDetection = (args) => {
    // Return if keyboard activated
    if (!args.pointerCoordinates) {
      return rectIntersection(args);
    }
    const { x, y } = args.pointerCoordinates;
    const { width, height } = args.collisionRect;
    const updated = {
      ...args,
      // The collision rectangle is broken when using snapCenterToCursor
      // Reset the collision rectangle based on pointer location and overlay size
      collisionRect: {
        width,
        height,
        bottom: y + height / 2,
        left: x - width / 2,
        right: x + width / 2,
        top: y - height / 2,
      },
    };
    return rectIntersection(updated);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          height: "53px",
          minHeight: "53px",
          width: "100%",
          borderBottom: dividerBorder,
          px: "16px",
          backgroundColor: surface,
        }}
      >
        <Button
          disableRipple
          startIcon={<ChevronLeftIcon />}
          onClick={onBack}
          sx={ModalCustomCancel}
        >
          {t("Back")}
        </Button>

        <GridLayoutPopover value={gridLayout} onChange={onGridLayoutChange} />

        <Typography fontSize="12px" fontWeight={600}>
          {selectedExamIds.size} {t("selected")}
        </Typography>
      </Box>

      <Box
        sx={{
          display:
            isFooterExpanded && hasExamsAvailableToSelect ? "none" : "block",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: "16px",
        }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={fixCursorSnapOffset}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={orderedExamIds}
            strategy={rectSortingStrategy}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${gridLayout.columns}, 1fr)`,
                gridTemplateRows: blockLayoutSizing.gridTemplateRows,
                gap: "8px",
              }}
            >
              {selectedExams.map((exam, index) => {
                const placement = cardPlacements[index] ?? {};

                return (
                  <ComparisonCard
                    key={exam.id}
                    selectedExam={exam}
                    isDragMode={activeId === exam.id}
                    isViewDetailsDisabled={isClickCooldownActive}
                    imageHeight={placement.imageHeight}
                    gridColumn={placement.gridColumn}
                    gridRow={placement.gridRow}
                    onDeselect={() => onToggleExamSelection(exam.id)}
                    onViewDetails={() => onViewExamDetails(exam.id)}
                  />
                );
              })}

              {blockLayoutSizing.dividerRows.map((row) => (
                <Box
                  key={`block-divider-${row}`}
                  sx={{
                    gridColumn: "1 / -1",
                    gridRow: `${row} / ${row + 1}`,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Divider
                    sx={{
                      width: "100%",
                      borderColor:
                        theme.palette.mode === "dark" ? "#202338" : "#5B6D7A50",
                    }}
                  />
                </Box>
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      </Box>

      {hasExamsAvailableToSelect && (
        <SelectExamsFooter
          selectedExamIds={selectedExamIds}
          onToggleExamSelection={onToggleExamSelection}
          footerState={footerState}
          onFooterStateChange={setFooterState}
        />
      )}
    </Box>
  );
};

export default ComparisonScreen;
